import * as React from 'react';
import {
    Container, Row, Col, Modal,
    Button, ListGroup, Form, FloatingLabel
} from 'react-bootstrap';
import useHelpers from '../../_useHelpers';
import { useAuth } from '../../_useAuth';
import { useAlert } from '../../_useAlert';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export default function Timeclock() {
    const [timesheets, setTimesheets] = React.useState([]);
    const [showEdit, setShowEdit] = React.useState(false);
    const [editTimesheet, setEditTimesheet] = React.useState(null);
    const { round2, formatCurrency } = useHelpers();
    const { curUser } = useAuth();
    const { alert, confirm } = useAlert();

    const refreshTimesheets = React.useCallback(function() {
        fetch('/api/timeclock/me', { method: "GET" }).then(
            (res) => {
                if(!res.ok) {
                    throw new Error(res.status + " " + res.statusText);
                } else {
                    return res.json();
                }
            }
        ).then(
            (result) => {
                setTimesheets(result);
            },
            (error) => {
                console.log(error);
                alert("Something went wrong",
                    <Row>
                        <Col>
                            Unable to load timesheets. Please refresh the page or try again later.
                        </Col>
                    </Row>,
                    () => {}
                );
            }
        );
    }, [alert]);

    React.useEffect(() => {
        refreshTimesheets();
    }, [refreshTimesheets]);

    function preClockin() {
        if(!curUser.userPositions) {
            alert("Unable to clock in",
                <Row>
                    <Col>
                        You have not been assigned a possition!
                    </Col>
                </Row>,
                () => {}
            );
        } else if(curUser.userPositions.length > 1) {
            confirm("Make a selection",
                <>
                    <Row className="mb-3">
                        <Col>
                            Which position are you clocking in for?
                        </Col>
                    </Row>
                    {curUser.userPositions && curUser.userPositions.map(function(position, index) {
                        var pos = curUser.positions.find(p => p.id === position.positionId);
                        return (<Form.Check 
                            key={index} 
                            id={"userPosition-" + position.id} 
                            type="radio" 
                            label={pos.event?.name + " - " + pos.name + " (" + formatCurrency(position.payrate) + "/hr)"}  
                            name="userposition" 
                            value={position.id} 
                            defaultChecked={index === 0} 
                        />);
                    })}
                </>,
                () => {
                    clockin(document.querySelector('input[name="userposition"]:checked').value);
                },
                () => {}
            );
        } else {
            clockin(curUser?.userPositions[0]?.id);
        }
    }

    function clockin(upId) {
        fetch('/api/clockin', { 
            method: "POST",
            headers: {
                'Content-Type': 'application/json'
            },
            body: upId.toString()
        }).then(
            (res) => {
                if(!res.ok) {
                    throw new Error(res.status + " " + res.statusText);
                } else {
                    return res.json();
                }
            }
        ).then(
            (result) => {
                setTimesheets([result, ...timesheets]);
            },
            (error) => {
                console.log(error);
                alert("Something went wrong",
                    <Row>
                        <Col>
                            Unable to clockin. Please refresh the page or try again later.
                        </Col>
                    </Row>,
                    () => {}
                );
            }
        );
    }

    function clockout() {
        fetch('/api/clockout', { method: "POST" }).then(
            (res) => {
                if(!res.ok) {
                    throw new Error(res.status + " " + res.statusText);
                } else {
                    return res.json();
                }
            }
        ).then(
            (result) => {
                refreshTimesheets();
            },
            (error) => {
                console.log(error);
                alert("Something went wrong",
                    <Row>
                        <Col>
                            Unable to clockout. Please refresh the page or try again later.
                        </Col>
                    </Row>,
                    () => {}
                );
            }
        );
    }

    function openEditModal(timesheet) {
        setEditTimesheet(timesheet);
        setShowEdit(true);
    }

    function closeEditModal() {
        setEditTimesheet(null);
        setShowEdit(false);
    }

    function saveTimesheet() {
        if(!(editTimesheet && editTimesheet?.id)) {
            alert("Something went wrong",
                <Row>
                    <Col>
                        Unable save timesheet. Please refresh the page or try again later.
                    </Col>
                </Row>,
                () => {}
            );
            return;
        }
        
        fetch('/api/timeclock/' + editTimesheet.id, { 
            method: "PATCH" ,
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                "startTime": editTimesheet.startTime,
                "endTime": editTimesheet.endTime || undefined,
                "note": editTimesheet.note || "",
                "cashTips": parseFloat(editTimesheet.cashTips || 0),
                "creditTips": parseFloat(editTimesheet.creditTips || 0)
            })
        }).then(
            (res) => {
                if(!res.ok) {
                    throw new Error(res.status + " " + res.statusText);
                } else {
                    return true;
                }
            }
        ).then(
            (success) => {
                refreshTimesheets();
                setEditTimesheet(null);
                setShowEdit(false);
            },
            (error) => {
                console.log(error);
                alert("Something went wrong",
                    <Row>
                        <Col>
                            Unable to save timesheet. Please refresh the page or try again later.
                        </Col>
                    </Row>,
                    () => {}
                );
            }
        );
    }

    function deleteTimesheet() {
        confirm("Are you sure?",
            <>
                <Row className="mb-3">
                    <Col>
                        Are you sure you want to delete this time entry? You cannot undo this action!
                    </Col>
                </Row>
            </>,
            () => {
                fetch('/api/timeclock/' + editTimesheet.id, { 
                    method: "DELETE" ,
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }).then(
                    (res) => {
                        if(!res.ok) {
                            throw new Error(res.status + " " + res.statusText);
                        } else {
                            return true;
                        }
                    }
                ).then(
                    (success) => {
                        refreshTimesheets();
                        setEditTimesheet(null);
                        setShowEdit(false);
                    },
                    (error) => {
                        console.log(error);
                        alert("Something went wrong",
                            <Row>
                                <Col>
                                    Unable to delete timesheet. Please refresh the page or try again later.
                                </Col>
                            </Row>,
                            () => {}
                        );
                    }
                );
            },
            () => {}
        );
    }

    function handleInput(e) {
        var key = e.target.getAttribute('name');
        var val = e.target.value;

        if(key === 'startTime' || key ==='endTime') {
            val = formatInTimeZone(new Date(e.target.value), 'GMT', "yyyy-MM-dd'T'HH:mm:ss.SSSX");
        }

        setEditTimesheet({
            ...editTimesheet,
            [key]: val
        });
    }

    return (<>
        <Container fluid>
            <Row className='justify-content-center my-5'>
                {timesheets.length ? 
                    !timesheets[0]?.endTime ? <>
                        <Col xs={8} sm={7} md={6} lg={5} xl={4}>
                            <div className="d-grid">
                                <Button variant="danger" size="lg" onClick={clockout}>Clock Out</Button>
                            </div>
                        </Col>
                    </> : <>
                        <Col xs={8} sm={7} md={6} lg={5} xl={4}>
                            <div className="d-grid">
                                <Button variant="success" size="lg" onClick={preClockin}>Clock In</Button>
                            </div>
                        </Col>
                    </> 
                : <>
                    <Col xs={8} sm={7} md={6} lg={5} xl={4}>
                        <div className="d-grid">
                            <Button variant="success" size="lg" onClick={preClockin}>Clock In</Button>
                        </div>
                    </Col>
                </>}
            </Row>
            <Row className='my-5'>
                <Col>
                    <ListGroup>
                        {timesheets.length ? timesheets.map(function(timesheet, index) {
                            const hours = round2((new Date(timesheet.endTime || new Date()).getTime() - new Date(timesheet.startTime).getTime()) / (1000 * 3600));
                            
                            return(
                                <ListGroup.Item key={index} className={{"bg-danger bg-opacity-50": !timesheet.endTime}}>
                                    <Row>
                                        <Col lg={5}>
                                            {timesheet.startTime && format(new Date(timesheet.startTime), 'M/d/yyyy h:mm:ss a')} - {timesheet.endTime && format(new Date(timesheet.endTime), 'M/d/yyyy h:mm:ss a')}
                                        </Col>
                                        <Col lg>
                                            {hours} Hours
                                        </Col>
                                        <Col lg>
                                            {timesheet.positionName} {formatCurrency(timesheet.payrate)}/hr
                                        </Col>
                                        <Col xs="auto" className="text-end">
                                            {!!timesheet.payrate && <>
                                                <span className="fw-bold">Pay: {formatCurrency(hours * timesheet.payrate)}</span>
                                                <br/>
                                            </>}
                                            {timesheet.isTipped && <>
                                                <span className="text-success">Cash Tips: {formatCurrency(timesheet.cashTips ?? 0)}</span>
                                                <br/>
                                                <span className="text-success">Credit Tips: {formatCurrency(timesheet.creditTips ?? 0)}</span>
                                                <br/>
                                            </>}
                                            {!!timesheet.deduction && <>
                                                <span className="text-danger">Deductions: &minus;{formatCurrency(timesheet.deduction ?? 0)}</span>
                                            </>}
                                        </Col>
                                        <Col xs="auto" className="ms-auto">
                                            <Button variant="link" onClick={() => { 
                                                openEditModal({...timesheet});
                                            }}>Edit</Button>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <p className="text-secondary text-prewrap">{timesheet.note}</p>
                                        </Col>
                                    </Row>
                                </ListGroup.Item>
                            );
                        }) : <>
                            <ListGroup.Item>
                                <Row className="justify-content-center">
                                    <Col xs="auto">
                                        No Timesheets Found!
                                    </Col>
                                </Row>
                            </ListGroup.Item>
                        </>}
                    </ListGroup>
                </Col>
            </Row>
            <Modal
                show={showEdit}
                size="lg"
                aria-labelledby="edit-timesheet-modal-title"
                backdrop="static"
                backdropClassName="session"
                className="session"
            >
                <Modal.Header>
                    <Modal.Title id="edit-timesheet-modal-title">
                        Edit Timesheet
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container fluid>
                        <Row className="justify-content-center mb-3">
                            <Col xs="auto">
                                <span className="h5"><span className="text-capitalize">{editTimesheet?.positionName}</span> {formatCurrency(editTimesheet?.payrate)}/hr</span>
                            </Col>
                        </Row>
                        <Row className="my-3">
                            <Col lg>
                                <FloatingLabel controlId="editTimesheetStart" label="Start">
                                    <Form.Control name="startTime" type="datetime-local" placeholder="Start" value={
                                        editTimesheet?.startTime ? format(new Date(editTimesheet?.startTime), "yyyy-MM-dd'T'HH:mm:ss.SSS") : ""
                                    } onChange={handleInput} />
                                </FloatingLabel>
                            </Col>
                            <Col lg className="mt-3 mt-lg-0">
                                <FloatingLabel controlId="editTimesheetEnd" label="End">
                                    <Form.Control name="endTime" type="datetime-local" placeholder="End" value={
                                        editTimesheet?.endTime ? format(new Date(editTimesheet?.endTime), "yyyy-MM-dd'T'HH:mm:ss.SSS") : ""
                                    } onChange={handleInput} />
                                </FloatingLabel>
                            </Col>
                        </Row>
                        {editTimesheet?.isTipped && <>
                            <Row className="my-3">
                                <Col>
                                    <FloatingLabel controlId="editTimesheetCashTips" label="Cash Tips">
                                        <Form.Control type="number" step="0.25" name="cashTips" placeholder="Cash Tips" value={editTimesheet?.cashTips ?? 0} onChange={handleInput} />
                                    </FloatingLabel>
                                </Col>   
                                <Col>
                                    <FloatingLabel controlId="editTimesheetCreditTips" label="Credit Tips">
                                        <Form.Control type="number" step="0.25" name="creditTips" placeholder="Credit Tips" value={editTimesheet?.creditTips ?? 0} onChange={handleInput} />
                                    </FloatingLabel>
                                </Col>        
                            </Row>
                        </>}
                        <Row>
                            <Col>
                                <FloatingLabel controlId="editTimesheetNote" label="Note">
                                    <Form.Control as="textarea" name="note" placeholder="Note" style={{ height: '150px' }} value={editTimesheet?.note || ""} onChange={handleInput} />
                                </FloatingLabel>
                            </Col>
                        </Row>
                        <Row className="justify-content-center mt-5">
                            <Col xs={6}>
                                <div className="d-grid">
                                    <Button type="button" variant="danger" onClick={deleteTimesheet}>Delete</Button>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button variant="primary" onClick={saveTimesheet}>Save</Button>
                    <Button variant="secondary" onClick={closeEditModal}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    </>);
}